import moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import Loader from '../../../../atoms/icons/loader';
import EmptyState from '../empty-state';
import './open-interest-external-widget.m.css';
import { isWithinTradingHours } from '../../../../../helpers/utils';
import { useSelector } from 'react-redux';
import get from 'lodash/get';

const OpenInterestExternalWidget = ({ widget = false }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [openInterestData, setOpenInterestData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('most-up-by-open-interest');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const getOpenInterestData = async (filterValue = 'most-up-by-open-interest') => {
    const response = await fetch(
      `/foapi/future/getByOi?order=${filterValue === 'most-up-by-open-interest' ? '1' : '0'}&limit=200`,
    );
    return response.json();
  };

  const resultOpenInterestData = useQuery(
    'getOpenInterestDataExternal',
    () => getOpenInterestData(selectedFilter),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setOpenInterestData(data?.data);
      },
      enabled:
        initialLoading ||
        (!!selectedFilter && isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (resultOpenInterestData?.isSuccess) {
      setOpenInterestData(resultOpenInterestData?.data?.data);
    }
  }, []);

  const clickHandler = async (type) => {
    setLoading(true);
    setSelectedFilter(type);
    const data = await getOpenInterestData(type);
    setOpenInterestData(data?.data);
    setLoading(false);
  };

  return (
    <React.Fragment>
      {initialLoading ? (
        <div styleName="initial-loading-container"></div>
      ) : (
        <div styleName="open-interest-container">
          <div styleName="btn-container">
            <button
              styleName={`filter-btn ${selectedFilter === 'most-up-by-open-interest' ? 'selected' : ''}`}
              onClick={() => clickHandler('most-up-by-open-interest')}
            >
              Open Interest Most Up
            </button>
            <button
              styleName={`filter-btn ${selectedFilter === 'most-down-by-open-interest' ? 'selected' : ''}`}
              onClick={() => clickHandler('most-down-by-open-interest')}
            >
              Open Interest Most Down
            </button>
          </div>
          {openInterestData && openInterestData?.length !== 0 ? (
            <>
              {loading ? (
                <div styleName="loading-container-ext">
                  <div styleName="loading-container-int">
                    <Loader color={'#dadee1'} />
                  </div>
                </div>
              ) : (
                <div styleName="table-body-container">
                  <table styleName="main-table">
                    <thead styleName="table-header">
                      <tr>
                        <th styleName="column-title">Security</th>
                        <th styleName="column-title-alt">Expiry</th>
                        <th styleName="column-title-alt">Spot price</th>
                        <th styleName="column-title-alt">
                          Premium/
                          <br />
                          Discount
                        </th>
                        <th styleName="column-title-alt">
                          Volume <br />
                          in contracts
                        </th>
                        <th styleName="column-title-alt">
                          Open Interest <br />
                          in contracts
                        </th>
                        <th styleName="column-title-alt">
                          OI
                          <br />
                          (%Chg)
                        </th>
                        <th styleName="column-title-alt">
                          Turnover
                          <br />
                          (Rs Cr)
                        </th>
                        <th styleName="column-title-alt">
                          Open Interest
                          <br /> Turnover(Rs Cr)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {openInterestData &&
                        openInterestData?.map((interestValue, index) => (
                          <tr styleName="data-row" key={index}>
                            <td>
                              {interestValue?.symbol ? (
                                <BQLink
                                  href={`/markets/futures-contract/${interestValue?.symbol}${widgetParam}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <span styleName="name">{interestValue?.symbol}</span>
                                </BQLink>
                              ) : (
                                <span styleName="name">-</span>
                              )}
                            </td>
                            <td styleName="column-value">
                              {moment(interestValue['expiry-date']).format('DD MMM')}
                            </td>
                            <td styleName="column-value">
                              {interestValue['spot-price'] ? interestValue['spot-price'] : '0'}
                            </td>
                            <td styleName="text-alignment">
                              {interestValue['premium-discount'] ? (
                                <span>
                                  {String(interestValue['premium-discount']).includes('-') ? (
                                    <span styleName="negative-value">
                                      {`${interestValue['premium-discount']?.toFixed(2)}`}
                                    </span>
                                  ) : (
                                    <span styleName="positive-value">
                                      {`+${interestValue['premium-discount']?.toFixed(2)}`}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td styleName="column-value">
                              {interestValue.volume ? interestValue.volume : '0'}
                            </td>
                            <td styleName="column-value">
                              {interestValue['open-interest']
                                ? interestValue['open-interest']
                                : '0'}
                            </td>
                            <td styleName="text-alignment">
                              {interestValue['open-interest-change-percentage'] ? (
                                <span>
                                  {String(
                                    interestValue['open-interest-change-percentage'],
                                  ).includes('-') ? (
                                    <span styleName="negative-value">
                                      {`${interestValue['open-interest-change-percentage']?.toFixed(2)}%`}
                                    </span>
                                  ) : (
                                    <span styleName="positive-value">
                                      {`+${interestValue['open-interest-change-percentage']?.toFixed(2)}%`}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span>-</span>
                              )}
                            </td>
                            <td styleName="column-value">
                              {interestValue.turnover
                                ? (interestValue.turnover / 10000000).toFixed(2)
                                : '0'}
                            </td>
                            <td styleName="column-value">
                              {interestValue['open-interest-turnover']
                                ? (interestValue['open-interest-turnover'] / 10000000).toFixed(2)
                                : '0'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default OpenInterestExternalWidget;
